import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Eye, X } from 'lucide-react';
import { useContent } from '../hooks/useContent';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

const FALLBACK_BGS = [
  'from-blue-600 to-blue-900',
  'from-slate-600 to-slate-900',
  'from-indigo-500 to-indigo-900',
  'from-pink-600 to-rose-900',
  'from-teal-600 to-teal-900',
  'from-violet-600 to-violet-900',
];

const FALLBACK = [
  { name: 'Rahul Gupta', quote: 'Got admission in 15 days', state: 'Delhi', year: '1st Year', city: 'Kazan', views: '4.3K' },
  { name: 'Mahak Kashyap', quote: 'Counselling helped shape my future', state: 'Punjab', year: '3rd Year', city: 'Kursk', views: '19.4K' },
  { name: 'Priyanka Nair', quote: 'No hidden fees, no surprises', state: 'Kerala', year: '2nd Year', city: 'Volgograd', views: '9.7K' },
  { name: 'Sneha Singh', quote: 'Took admission without any hassle', state: 'UP', year: '4th Year', city: 'Rostov', views: '6K' },
  { name: 'Amit Verma', quote: 'Best decision of my medical career', state: 'Bihar', year: '5th Year', city: 'Bashkir', views: '7.2K' },
  { name: 'Pooja Sharma', quote: 'FMGE cleared on first attempt', state: 'Rajasthan', year: 'Completed', city: 'Moscow', views: '11.8K' },
];

export default function TrustedVoices() {
  const voices = useContent('trustedVoices', FALLBACK) || FALLBACK;
  const list = (voices && voices.length) ? voices : FALLBACK;
  const [playing, setPlaying] = useState(null);
  const ref = useRef(null);
  const scroll = (dir) => { if (ref.current) ref.current.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' }); };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto max-w-7xl px-4 md:px-8">
        <div className="mb-10">
          <p className="text-sm font-bold text-secondary uppercase tracking-widest mb-1">Trusted Voices</p>
          <h3 className="font-serif text-4xl font-bold text-primary">
            Hear them <span className="text-brand">out</span>
          </h3>
        </div>

        <div className="relative">
          <button onClick={() => scroll('left')} aria-label="Scroll left"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll('right')} aria-label="Scroll right"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all">
            <ChevronRight className="h-4 w-4" />
          </button>

          <div ref={ref} className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 md:-mx-8 md:px-8 snap-x snap-mandatory scrollbar-hide">
            {list.map((v, i) => {
              const poster = resolveImg(v.poster);
              const bg = FALLBACK_BGS[i % FALLBACK_BGS.length];
              const canPlay = !!v.youtubeId;
              return (
              <div key={i} className="shrink-0 snap-start flex flex-col items-center gap-3 w-44">
                <div
                  onClick={() => canPlay && setPlaying(v.youtubeId)}
                  className={`relative w-44 h-80 rounded-[2rem] border-4 border-foreground/20 bg-foreground/10 overflow-hidden shadow-xl group ${canPlay ? 'cursor-pointer' : ''}`}
                >
                  {poster ? (
                    <img src={poster} alt={v.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className={`absolute inset-0 bg-gradient-to-b ${bg}`} />
                  )}
                  <div className="absolute inset-0 bg-black/25" />
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-2 bg-black/40 rounded-full z-10" />

                  {v.views && (
                    <div className="absolute top-4 left-3 z-10 flex items-center gap-1 bg-black/50 text-white text-xs font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
                      <Eye className="h-3 w-3" /> {v.views}
                    </div>
                  )}

                  <div className="absolute top-4 right-3 z-10">
                    <div className="bg-brand/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm">RE</div>
                  </div>

                  <div className="absolute bottom-14 left-0 right-0 px-3 z-10">
                    <p className="text-white font-bold text-sm leading-tight drop-shadow-lg">{v.quote}</p>
                  </div>
                  <div className="absolute bottom-6 left-3 right-3 z-10">
                    <p className="text-white/90 text-xs font-semibold uppercase tracking-wide">{v.name}</p>
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="h-14 w-14 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/60 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="h-6 w-6 text-white ml-1" fill="currentColor" strokeWidth={0} />
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-xs font-semibold text-foreground">{v.name}</p>
                  <p className="text-xs text-muted-foreground">{v.year}{v.city ? ` · ${v.city}` : ''}</p>
                  <p className="text-xs text-brand font-medium">{v.state}</p>
                </div>
              </div>
            );})}
          </div>
        </div>
      </div>

      {playing && (
        <div
          onClick={() => setPlaying(null)}
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
        >
          <button aria-label="Close" className="absolute top-6 right-6 text-white/80 hover:text-white">
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-md aspect-[9/16] bg-black rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://www.youtube.com/embed/${playing}?autoplay=1&rel=0`}
              title="Trusted voice"
              allow="autoplay; encrypted-media"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </section>
  );
}
